import { convertIntervalsToReadableFormat } from '@/services/db/actions/variable/utils/convertIntervalsToReadableFormat'
import {
  formatStoredTransition,
  formatTransitionBoundaryLabel,
  isSameTransitionFunction,
  StoredTransitionAtBoundary,
} from '@/utils/network/transitionFunctions'

import { addIndentationToLines, getDiffOfTexts } from './textDiffFormatting'
import {
  generateVariableChanges,
  generateVariableList,
} from './variableChangeRenderer'
import {
  ChangedArrowCommentProps,
  ChangedGranularityProps,
  ChangedIntervalsProps,
  ChangedStatesProps,
  ChangedTagsProps,
  ChangedTransitionFunctionsProps,
  TextFieldChangeProps,
  TransitionBoundaryDiffRow,
  VariableDiffs,
  VariablePropsWithDiff,
  VariableProps,
  VariablesByType,
  VariablesDiffs,
} from './variableDiffTypes'

const initialVariableDiff: VariableDiffs = {
  name: undefined,
  type: undefined,
  intervalUnit: undefined,
  intervals: undefined,
  transitionFunctions: undefined,
  state: undefined,
  optimalStateName: undefined,
  probabilities: undefined,
  granularity: undefined,
  comment: undefined,
  tags: undefined,
  definition: undefined,
  references: undefined,
  formula: undefined,
  prevalenceExplanation: undefined,
  arrowComment: undefined,
  parameterizationType: undefined,
}

// A boundary that exists on one side only, because a state was added or
// removed (or renamed, which moves the boundary's identity here since this
// module keys boundaries by state name).
const ABSENT_BOUNDARY = 'None'

const renderBoundary = (
  boundary: StoredTransitionAtBoundary | undefined,
): string =>
  boundary
    ? formatStoredTransition(boundary.transition, boundary.boundary)
    : ABSENT_BOUNDARY

// Compared on the stored values rather than on the two rendered strings: the
// rendering is lossy in principle (any two configs that format alike would
// collapse into "no row" and silently drop a change the detector found).
const isSameBoundary = (
  a: StoredTransitionAtBoundary | undefined,
  b: StoredTransitionAtBoundary | undefined,
): boolean => {
  if (!a || !b) return false
  if (!a.transition || !b.transition) return a.transition === b.transition
  return isSameTransitionFunction(a.transition, b.transition)
}

function buildTransitionBoundaryDiffRows(
  from: StoredTransitionAtBoundary[],
  to: StoredTransitionAtBoundary[],
): TransitionBoundaryDiffRow[] {
  const fromByKey = new Map(from.map((entry) => [entry.boundaryKey, entry]))
  const toByKey = new Map(to.map((entry) => [entry.boundaryKey, entry]))

  const rows: TransitionBoundaryDiffRow[] = []
  const pushRow = (
    boundary: StoredTransitionAtBoundary,
    fromEntry: StoredTransitionAtBoundary | undefined,
    toEntry: StoredTransitionAtBoundary | undefined,
  ) => {
    if (isSameBoundary(fromEntry, toEntry)) return
    rows.push({
      boundaryLabel: formatTransitionBoundaryLabel(boundary),
      from: renderBoundary(fromEntry),
      to: renderBoundary(toEntry),
    })
  }

  to.forEach((boundary) =>
    pushRow(boundary, fromByKey.get(boundary.boundaryKey), boundary),
  )
  from.forEach((boundary) => {
    if (toByKey.has(boundary.boundaryKey)) return
    pushRow(boundary, boundary, undefined)
  })
  return rows
}

export class VariableDiffBuilder {
  private deletedVariablesByType: VariablesByType = new Map()
  private addedVariablesByType: VariablesByType = new Map()
  private newPublishedVariablesByType: VariablesByType = new Map()
  private unpublishedVariablesByType: VariablesByType = new Map()
  private variablesChangesByType: VariablesDiffs = new Map()

  addDeletedVariable({ type, variableName }: VariableProps): void {
    if (!this.deletedVariablesByType.has(type)) {
      this.deletedVariablesByType.set(type, [])
    }
    const variables = this.deletedVariablesByType.get(type)!
    variables.push(variableName)
  }

  addAddedVariable({ type, variableName }: VariableProps): void {
    if (!this.addedVariablesByType.has(type)) {
      this.addedVariablesByType.set(type, [])
    }
    const variables = this.addedVariablesByType.get(type)!
    variables.push(variableName)
  }

  addNewPublishedVariable({ type, variableName }: VariableProps): void {
    if (!this.newPublishedVariablesByType.has(type)) {
      this.newPublishedVariablesByType.set(type, [])
    }
    const variables = this.newPublishedVariablesByType.get(type)!
    variables.push(variableName)
  }

  addUnpublishedVariable({ type, variableName }: VariableProps): void {
    if (!this.unpublishedVariablesByType.has(type)) {
      this.unpublishedVariablesByType.set(type, [])
    }
    const variables = this.unpublishedVariablesByType.get(type)!
    variables.push(variableName)
  }

  addChangedName({
    type,
    variableName,
    from,
    to,
  }: VariablePropsWithDiff): void {
    const variables = this.getVariablesByType(type)
    variables.set(variableName, {
      ...this.getInitialVariableDiff({ variables, variableName }),
      name: { from, to },
    })
  }

  addChangedType({
    type,
    variableName,
    from,
    to,
  }: VariablePropsWithDiff): void {
    const variables = this.getVariablesByType(type)
    variables.set(variableName, {
      ...this.getInitialVariableDiff({ variables, variableName }),
      type: { from, to },
    })
  }

  addChangedIntervalUnit({
    type,
    variableName,
    from,
    to,
  }: VariablePropsWithDiff): void {
    const variables = this.getVariablesByType(type)
    variables.set(variableName, {
      ...this.getInitialVariableDiff({ variables, variableName }),
      intervalUnit: { from, to },
    })
  }

  addChangedIntervals({
    type,
    variableName,
    from,
    to,
    fromStates,
    toStates,
  }: ChangedIntervalsProps): void {
    const readableFromIntervals = from
      ? convertIntervalsToReadableFormat(from, fromStates)
      : 'None'
    const readableToIntervals = to
      ? convertIntervalsToReadableFormat(to, toStates)
      : 'None'
    const variables = this.getVariablesByType(type)
    variables.set(variableName, {
      ...this.getInitialVariableDiff({ variables, variableName }),
      intervals: { from: readableFromIntervals, to: readableToIntervals },
    })
  }

  addChangedTransitionFunctions({
    type,
    variableName,
    from,
    to,
  }: ChangedTransitionFunctionsProps): void {
    const rows = buildTransitionBoundaryDiffRows(from, to)
    if (rows.length === 0) return
    const variables = this.getVariablesByType(type)
    variables.set(variableName, {
      ...this.getInitialVariableDiff({ variables, variableName }),
      transitionFunctions: { rows },
    })
  }

  addChangedParameterizationType({
    type,
    variableName,
    from,
    to,
  }: VariablePropsWithDiff): void {
    const variables = this.getVariablesByType(type)
    variables.set(variableName, {
      ...this.getInitialVariableDiff({ variables, variableName }),
      parameterizationType: { from, to },
    })
  }

  addChangedStates({ type, variableName, from, to }: ChangedStatesProps): void {
    const variables = this.getVariablesByType(type)
    variables.set(variableName, {
      ...this.getInitialVariableDiff({ variables, variableName }),
      state: { from, to },
    })
  }

  addChangedOptimalState({
    type,
    variableName,
    from,
    to,
  }: VariablePropsWithDiff): void {
    const variables = this.getVariablesByType(type)
    variables.set(variableName, {
      ...this.getInitialVariableDiff({ variables, variableName }),
      optimalStateName: { from, to },
    })
  }

  addProbabilitiesChanged({ type, variableName }: VariableProps): void {
    const variables = this.getVariablesByType(type)
    variables.set(variableName, {
      ...this.getInitialVariableDiff({ variables, variableName }),
      probabilities: 'changed',
    })
  }

  addChangedGranularity({
    type,
    variableName,
    from,
    to,
  }: ChangedGranularityProps): void {
    const variables = this.getVariablesByType(type)
    variables.set(variableName, {
      ...this.getInitialVariableDiff({ variables, variableName }),
      granularity: { from, to },
    })
  }

  addChangedComment({
    type,
    variableName,
    from,
    to,
    lineSize,
  }: TextFieldChangeProps): void {
    const variables = this.getVariablesByType(type)
    const textDiff = getDiffOfTexts({ from, to, lineSize })
    const indentedTextDiff = addIndentationToLines(textDiff, 4)
    variables.set(variableName, {
      ...this.getInitialVariableDiff({ variables, variableName }),
      comment: {
        textDiff: indentedTextDiff,
      },
    })
  }

  addChangedTags({ type, variableName, from, to }: ChangedTagsProps): void {
    const variables = this.getVariablesByType(type)
    variables.set(variableName, {
      ...this.getInitialVariableDiff({ variables, variableName }),
      tags: {
        from: from ?? [],
        to: to ?? [],
      },
    })
  }

  addChangedDefinition({
    type,
    variableName,
    from,
    to,
    lineSize,
  }: TextFieldChangeProps): void {
    const variables = this.getVariablesByType(type)
    const textDiff = getDiffOfTexts({ from, to, lineSize })
    const indentedTextDiff = addIndentationToLines(textDiff, 4)
    variables.set(variableName, {
      ...this.getInitialVariableDiff({ variables, variableName }),
      definition: {
        textDiff: indentedTextDiff,
      },
    })
  }

  addChangedReferences({
    type,
    variableName,
    from,
    to,
    lineSize,
  }: TextFieldChangeProps): void {
    const variables = this.getVariablesByType(type)
    const textDiff = getDiffOfTexts({ from, to, lineSize })
    const indentedTextDiff = addIndentationToLines(textDiff, 4)
    variables.set(variableName, {
      ...this.getInitialVariableDiff({ variables, variableName }),
      references: {
        textDiff: indentedTextDiff,
      },
    })
  }

  addChangedFormula({
    type,
    variableName,
    from,
    to,
    lineSize,
  }: TextFieldChangeProps): void {
    const variables = this.getVariablesByType(type)
    const textDiff = getDiffOfTexts({ from, to, lineSize })
    const indentedTextDiff = addIndentationToLines(textDiff, 4)
    variables.set(variableName, {
      ...this.getInitialVariableDiff({ variables, variableName }),
      formula: {
        textDiff: indentedTextDiff,
      },
    })
  }

  addChangedPrevalenceExplanation({
    type,
    variableName,
    from,
    to,
    lineSize,
  }: TextFieldChangeProps): void {
    const variables = this.getVariablesByType(type)
    const textDiff = getDiffOfTexts({ from, to, lineSize })
    const indentedTextDiff = addIndentationToLines(textDiff, 4)
    variables.set(variableName, {
      ...this.getInitialVariableDiff({ variables, variableName }),
      prevalenceExplanation: {
        textDiff: indentedTextDiff,
      },
    })
  }

  addChangedArrowComment({
    type,
    variableName,
    from,
    to,
    inboundVariableName,
    lineSize,
  }: ChangedArrowCommentProps): void {
    const variables = this.getVariablesByType(type)
    const variable = variables.get(variableName)
    const arrowComment = variable?.arrowComment ?? []
    const textDiff = getDiffOfTexts({
      from: from ?? 'It is an added arrow.',
      to: to ?? 'It is a removed arrow.',
      lineSize,
    })
    const indentedTextDiff = addIndentationToLines(textDiff, 5)
    arrowComment.push({
      textDiff: indentedTextDiff,
      inboundVariableName,
    })
    variables.set(variableName, {
      ...(variable ?? initialVariableDiff),
      arrowComment,
    })
  }

  renderDeletedVariableList(): string {
    return generateVariableList(this.deletedVariablesByType)
  }

  renderAddedVariableList(): string {
    return generateVariableList(this.addedVariablesByType)
  }

  renderUnpublishedVariableList(): string {
    return generateVariableList(this.unpublishedVariablesByType)
  }

  renderNewPublishedVariableList(): string {
    return generateVariableList(this.newPublishedVariablesByType)
  }

  renderVariableChanges(): string {
    return generateVariableChanges(this.variablesChangesByType)
  }

  private getInitialVariableDiff = ({
    variables,
    variableName,
  }: {
    variables: Map<string, VariableDiffs>
    variableName: string
  }): VariableDiffs => variables.get(variableName) ?? initialVariableDiff

  private getVariablesByType = (type: string): Map<string, VariableDiffs> => {
    if (!this.variablesChangesByType.has(type)) {
      this.variablesChangesByType.set(type, new Map())
    }
    const variables = this.variablesChangesByType.get(type)!
    return variables
  }
}
